/**
 * Variation Matrix Utility
 * Builds and manages efficient pricing lookup from Etsy inventory data
 */

import logger from './logger.js';

/**
 * Extract size name from Etsy size string
 * Examples: "SMALL height 1.2 inches" -> "SMALL"
 */
const extractSizeName = (sizeString) => {
  if (!sizeString) return null;
  const match = sizeString.match(/^(\w+)/);
  return match ? match[1].toUpperCase() : null;
};

/**
 * Comprehensive size normalization map
 * Maps all possible size variations to their canonical forms
 */
const SIZE_VARIANTS = {
  // Micro variants
  'MICRO': ['MICRO', 'MICRO'],
  'MINI': ['MINI', 'MICRO'],
  
  // Small variants  
  'SMALL': ['SMALL', 'SM', 'S'],
  'SM': ['SMALL', 'SM', 'S'],
  'S': ['SMALL', 'SM', 'S'],
  
  // Medium variants
  'MEDIUM': ['MEDIUM', 'MED', 'M'],
  'MED': ['MEDIUM', 'MED', 'M'],
  'M': ['MEDIUM', 'MED', 'M'],
  
  // Large variants
  'LARGE': ['LARGE', 'LG', 'L'],
  'LG': ['LARGE', 'LG', 'L'],
  'L': ['LARGE', 'LG', 'L'],
  
  // Extra Large variants
  'XLARGE': ['XLARGE', 'XL', 'EXTRA LARGE'],
  'XL': ['XLARGE', 'XL', 'EXTRA LARGE'],
  'EXTRA LARGE': ['XLARGE', 'XL', 'EXTRA LARGE'],
  
  // Jumbo variants
  'JUMBO': ['JUMBO', 'JUMBO'],
};

/**
 * Cache for available sizes to avoid repeated parsing
 */
let availableSizesCache = new WeakMap();

/**
 * Get available sizes from variation matrix with caching
 */
const getAvailableSizesFromMatrix = (variationMatrix) => {
  if (!variationMatrix) return new Set();
  
  // Check cache first
  if (availableSizesCache.has(variationMatrix)) {
    return availableSizesCache.get(variationMatrix);
  }
  
  // Parse sizes from matrix keys
  const availableSizes = new Set();
  Object.keys(variationMatrix).forEach(key => {
    const [size] = key.split('|');
    if (size) availableSizes.add(size);
  });
  
  // Cache the result
  availableSizesCache.set(variationMatrix, availableSizes);
  return availableSizes;
};

/**
 * Find the best size match from available variation matrix keys
 * This is the robust approach that adapts to actual data
 */
const findBestSizeMatch = (inputSize, variationMatrix) => {
  if (!inputSize || !variationMatrix) return inputSize;
  
  const normalizedInput = inputSize.toUpperCase().trim();
  const availableSizes = getAvailableSizesFromMatrix(variationMatrix);
  
  // First, try exact match
  if (availableSizes.has(normalizedInput)) {
    return normalizedInput;
  }
  
  // Try to find a match using size variants
  const possibleVariants = SIZE_VARIANTS[normalizedInput] || [normalizedInput];
  
  for (const variant of possibleVariants) {
    if (availableSizes.has(variant)) {
      return variant;
    }
  }
  
  // Try reverse lookup - check if any available size has our input as a variant
  for (const availableSize of availableSizes) {
    const variants = SIZE_VARIANTS[availableSize] || [availableSize];
    if (variants.includes(normalizedInput)) {
      return availableSize;
    }
  }
  
  
  // Return original if no match found
  return normalizedInput;
};

/**
 * Map any size input to the format actually used in the variation matrix
 * This replaces the old mapSizeToEtsy function with a smarter approach
 */
const mapSizeToEtsy = (inputSize, variationMatrix) => {
  return findBestSizeMatch(inputSize, variationMatrix);
};

/**
 * Map ClothedEye color names to Etsy color names
 * COMPREHENSIVE: Handle all ClothedEye UI color variations
 */
const mapColorToEtsy = (clothedEyeColor) => {
  if (!clothedEyeColor) return clothedEyeColor;
  
  // COMPLETE mapping from ClothedEye colors to Etsy colors
  const colorMapping = {
    // Matte colors (remove parenthetical)
    'Black (Matte)': 'Black',
    'White (Matte)': 'White',
    
    // Metallic colors (remove "Metallic" suffix)
    'Silver Metallic': 'Silver',
    'Gold Metallic': 'Gold', 
    'Copper Metallic': 'Copper',
    
    // Violet variations
    'Violet': 'Violet',
    'Violet (Grape)': 'Violet',
    
    // Holographic colors (keep as-is)
    'Gray Holographic': 'Gray Holographic',
    
    // Standard colors that should match exactly
    'Black': 'Black',
    'White': 'White',
    'Gray': 'Gray', 
    'Red': 'Red',
    'Orange': 'Orange',
    'Yellow': 'Yellow',
    'Dozer Yellow': 'Dozer Yellow',
    'Green': 'Green',
    'Forest Green': 'Forest Green',
    'Ice Blue': 'Ice Blue',
    'Sky Blue': 'Sky Blue', 
    'Blue': 'Blue',
    'Purple': 'Purple',
    'Pink': 'Pink',
    'Silver': 'Silver',
    'Gold': 'Gold',
    'Copper': 'Copper',
    'Nut Brown': 'Nut Brown',
    'Brown': 'Brown'
  };
  
  const etsyColor = colorMapping[clothedEyeColor] || clothedEyeColor;
  return etsyColor;
};

/**
 * Map Etsy color names back to ClothedEye color names (reverse mapping)
 */
const mapColorFromEtsy = (etsyColor) => {
  if (!etsyColor) return etsyColor;
  
  // Reverse mapping from Etsy colors to ClothedEye colors
  const reverseColorMapping = {
    'Black': 'Black (Matte)',
    'White': 'White (Matte)', 
    'Silver': 'Silver Metallic',
    'Gold': 'Gold Metallic',
    'Copper': 'Copper Metallic',
    'Gray Holographic': 'Gray Holographic', // Stays the same
    'Violet': 'Violet', // Could be Violet or Violet (Grape) - use simple form
    // All other colors map directly (no change needed)
    'Gray': 'Gray',
    'Red': 'Red',
    'Orange': 'Orange', 
    'Yellow': 'Yellow',
    'Dozer Yellow': 'Dozer Yellow',
    'Green': 'Green',
    'Forest Green': 'Forest Green',
    'Ice Blue': 'Ice Blue',
    'Sky Blue': 'Sky Blue',
    'Blue': 'Blue', 
    'Purple': 'Purple',
    'Pink': 'Pink',
    'Nut Brown': 'Nut Brown',
    'Brown': 'Brown'
  };
  
  return reverseColorMapping[etsyColor] || etsyColor;
};

/**
 * Build variation matrix from Etsy inventory data
 * @param {Object} inventoryData - Etsy inventory.products array
 * @returns {Object} - Variation matrix for fast lookups
 */
export const buildVariationMatrix = (inventoryData) => {
  const matrix = {};
  
  if (!inventoryData?.products || !Array.isArray(inventoryData.products)) {
    logger.warn('⚠️ No inventory products data provided for variation matrix');
    return matrix;
  }

  inventoryData.products.forEach((product, index) => {
    try {
      let sizeName = null;
      let colorName = null;

      // Extract size and color from property_values
      product.property_values?.forEach(prop => {
        if (prop.property_name === 'Size' || prop.property_name === 'Style') {
          if (prop.values && prop.values.length > 0) {
            sizeName = extractSizeName(prop.values[0]);
          }
        } else if (prop.property_name === 'Primary color') {
          if (prop.values && prop.values.length > 0) {
            colorName = prop.values[0];
          }
        }
      });

      // Create variation entry if we have both size and color
      if (sizeName && colorName && product.offerings && product.offerings.length > 0) {
        const offering = product.offerings[0];
        const key = `${sizeName}|${colorName}`;
        
        matrix[key] = {
          size: sizeName,
          color: colorName,
          price: offering.price ? (offering.price.amount / offering.price.divisor) : null,
          quantity: offering.quantity || 0,
          product_id: product.product_id,
          offering_id: offering.offering_id,
          sku: product.sku,
          isEnabled: offering.is_enabled || false,
          currency: offering.price?.currency_code || 'USD'
        };
      }
    } catch (error) {
      logger.warn(`⚠️ Error processing product ${index}:`, error);
    }
  });

  return matrix;
};

/**
 * Get price for specific size/color combination
 * @param {Object} variationMatrix - Built variation matrix
 * @param {string} size - Size name (e.g., "SMALL")
 * @param {string} color - Color name (ClothedEye format)
 * @returns {number|null} - Price in dollars or null if not found
 */
export const getVariationPrice = (variationMatrix, size, color) => {
  if (!variationMatrix || !size || !color) {
    return null;
  }

  // Map ClothedEye size and color to Etsy equivalents using smart matching
  const etsySize = mapSizeToEtsy(size.toUpperCase(), variationMatrix);
  const etsyColor = mapColorToEtsy(color);
  const key = `${etsySize}|${etsyColor}`;
  
  
  const variation = variationMatrix[key];
  if (variation && variation.price !== null) {
    return variation.price;
  }

  
  // Try to find similar keys for debugging
  const similarKeys = Object.keys(variationMatrix).filter(k => 
    k.includes(etsySize) || k.includes(etsyColor)
  );
  if (similarKeys.length > 0) {
  }
  
  return null;
};

/**
 * Get all available sizes from variation matrix
 */
export const getAvailableSizes = (variationMatrix) => {
  const sizes = new Set();
  Object.values(variationMatrix).forEach(variation => {
    if (variation.quantity > 0 && variation.isEnabled) {
      sizes.add(variation.size);
    }
  });
  return Array.from(sizes);
};

/**
 * Get all available colors for a specific size
 */
export const getAvailableColorsForSize = (variationMatrix, size) => {
  const etsySize = mapSizeToEtsy(size.toUpperCase(), variationMatrix);
  const colors = new Set();
  Object.values(variationMatrix).forEach(variation => {
    if (variation.size === etsySize && variation.quantity > 0 && variation.isEnabled) {
      // Convert Etsy color back to ClothedEye color name for UI consistency
      const clothedEyeColor = mapColorFromEtsy(variation.color);
      colors.add(clothedEyeColor);
    }
  });
  return Array.from(colors);
};

/**
 * Get inventory quantity for specific size/color combination
 */
export const getVariationQuantity = (variationMatrix, size, color) => {
  const etsySize = mapSizeToEtsy(size.toUpperCase(), variationMatrix);
  const etsyColor = mapColorToEtsy(color);
  const key = `${etsySize}|${etsyColor}`;
  const variation = variationMatrix[key];
  return variation ? variation.quantity : 0;
};

/**
 * Check if a size/color combination is available
 */
export const isVariationAvailable = (variationMatrix, size, color) => {
  const quantity = getVariationQuantity(variationMatrix, size, color);
  return quantity > 0;
};

/**
 * Debug function to analyze size mapping issues
 */
export const debugSizeMapping = (variationMatrix, testSizes = ['MEDIUM', 'MED', 'M', 'SMALL', 'SM', 'S']) => {
  // Debug function disabled for performance
};

/**
 * Debug function to log all variations in matrix
 */
export const debugVariationMatrix = (variationMatrix, title = 'Variation Matrix') => {
  // Debug function disabled for performance
};
